import { useSubchapterValidations } from "@/hooks/useSubchapterValidations";
import {
  NOT_STARTED,
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { SelfReflectionAnswerRecord } from "@/lib/selfReflectionAnswers";
import type {
  SelfReflectionAnswerState,
  SelfReflectionRunnerState,
} from "@/components/storyboard/selfreflection/SelfReflectionRunner";

export interface SelfReflectionBackendState {
  loaded: boolean;
  status: InteractiveElementStatus;
  reviewRecord: SelfReflectionAnswerRecord;
  initialPartial?: SelfReflectionRunnerState;
}

const LOADING: SelfReflectionBackendState = {
  loaded: false,
  status: NOT_STARTED,
  reviewRecord: { answers: {} },
};

const NOT_STARTED_LOADED: SelfReflectionBackendState = {
  loaded: true,
  status: NOT_STARTED,
  reviewRecord: { answers: {} },
};

export const useSelfReflectionValidations = (
  contentElementId: string,
  questionCount: number
): SelfReflectionBackendState => {
  const { data, isLoading, isEnabled } = useSubchapterValidations(
    contentElementId,
    questionCount > 0
  );

  if (!isEnabled) return NOT_STARTED_LOADED;
  if (isLoading) return LOADING;

  // A failed request reads as "nothing saved yet"; the learner can still answer.
  const completed = (data ?? [])
    .filter(
      (i) => i.contentElementId === contentElementId && i.status === "completed"
    )
    .sort((a, b) => a.entryIndex - b.entryIndex);

  return derive(completed, questionCount);
};

const derive = (
  completed: Array<{
    entryIndex: number;
    userInput: string;
    result: { feedback?: string };
  }>,
  questionCount: number
): SelfReflectionBackendState => {
  const reviewRecord: SelfReflectionAnswerRecord = { answers: {} };
  const runnerAnswers: Record<string, SelfReflectionAnswerState> = {};
  for (const it of completed) {
    const key = String(it.entryIndex);
    reviewRecord.answers[key] = {
      value: it.userInput,
      feedback: it.result.feedback ?? "",
    };
    runnerAnswers[key] = {
      value: it.userInput,
      submitted: true,
      feedback: it.result.feedback ?? "",
    };
  }

  if (completed.length === 0) {
    return { loaded: true, status: NOT_STARTED, reviewRecord };
  }

  if (completed.length >= questionCount) {
    return {
      loaded: true,
      status: {
        kind: InteractiveElementStatusKind.Completed,
        attempts: 1,
        completedAt: "",
      },
      reviewRecord,
    };
  }

  const answered = new Set(completed.map((i) => i.entryIndex));
  let currentIndex = 0;
  while (answered.has(currentIndex) && currentIndex < questionCount - 1) {
    currentIndex++;
  }
  const initialPartial: SelfReflectionRunnerState = {
    currentIndex,
    answers: runnerAnswers,
  };
  return {
    loaded: true,
    status: {
      kind: InteractiveElementStatusKind.InProgress,
      partial: null,
    },
    reviewRecord,
    initialPartial,
  };
};
